import { useState, useEffect, Fragment } from "react";
import { Alert, AlertDescription } from "@/ui/components/primitives/alert";
import { Button } from "@/ui/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/primitives/dialog";
import { Input } from "@/ui/components/primitives/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/ui/components/primitives/input-otp";
import { Label } from "@/ui/components/primitives/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/ui/components/primitives/tabs";
import { Badge } from "@/ui/components/primitives/badge";
import { Copy, Check, Shield, QrCode } from "lucide-react";
import { setupMFA, verifyMFA } from "@/services/api/services/mfa";
import type { MFASetupData } from "@/modules/shared/types";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";

interface MFASetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMFAEnabled: () => void;
}

export function MFASetupModal({ open, onOpenChange, onMFAEnabled }: MFASetupModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [mfaData, setMfaData] = useState<MFASetupData | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState<number[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setMfaData(null);
      setVerificationCode("");
      setCopiedSecret(false);
      setCopiedCodes([]);
    }
  }, [open]);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await setupMFA("TOTP");
      if (response.success) {
        setMfaData(response.data);
        setStep(2);
      }
    } catch (error: any) {
      showErrorToast("Failed to setup MFA: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      showErrorToast("Please enter a 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await verifyMFA(verificationCode);
      if (response.success) {
        showSuccessToast("MFA enabled successfully!");
        setStep(3);
        onMFAEnabled();
      }
    } catch (error: any) {
      showErrorToast("Failed to verify code: " + (error.message || "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: "secret" | "code", index?: number) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "secret") {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else if (type === "code" && index !== undefined) {
        setCopiedCodes(prev => [...prev, index]);
        setTimeout(() => setCopiedCodes(prev => prev.filter(i => i !== index)), 2000);
      }
    } catch (error) {
      showErrorToast("Failed to copy to clipboard");
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Fragment>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md w-full mx-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication Setup
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Enable an extra layer of security for your account"}
            {step === 2 && "Scan the QR code with your authenticator app"}
            {step === 3 && "Save your backup codes"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-6">
            <Alert>
              <QrCode className="h-4 w-4" />
              <AlertDescription>
                Two-factor authentication adds an extra layer of security to your account.
                You'll need to enter a code from your authenticator app when signing in.
              </AlertDescription>
            </Alert>

            <div className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">Before you continue:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Make sure you have an authenticator app installed (Google Authenticator, Authy, etc.)</li>
                  <li>• Keep your backup codes in a safe place</li>
                  <li>• Test your setup before closing this window</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSetup} disabled={loading}>
                {loading ? "Setting up..." : "Continue"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && mfaData && (
          <div className="space-y-6">
            <Tabs defaultValue="qr" className="w-full">
              <TabsList className="inline-flex w-full justify-center">
                <TabsTrigger value="qr">QR Code</TabsTrigger>
                <TabsTrigger value="manual">Manual Entry</TabsTrigger>
              </TabsList>

              <TabsContent value="qr" className="space-y-4">
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img
                    src={mfaData.qrCode}
                    alt="QR Code for MFA Setup"
                    className="w-48 h-48"
                  />
                </div>
                <Alert>
                  <AlertDescription>
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </AlertDescription>
                </Alert>
              </TabsContent>

              <TabsContent value="manual" className="space-y-4">
                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <div className="flex gap-2">
                    <Input
                      value={mfaData.secret}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(mfaData.secret, "secret")}
                    >
                      {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enter this secret key manually in your authenticator app
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <div className="space-y-4 py-4">
              <Label>Enter Verification Code</Label>
              <div className="flex justify-center px-8">
                <InputOTP
                  value={verificationCode}
                  onChange={setVerificationCode}
                  maxLength={6}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleVerify} disabled={loading || verificationCode.length !== 6}>
                {loading ? "Verifying..." : "Verify & Enable"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && mfaData && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5 text-success" />
                <h4 className="font-medium">MFA Enabled Successfully!</h4>
              </div>

              <Alert>
                <AlertDescription>
                  Your account is now protected with two-factor authentication.
                  Save these backup codes in a secure location. You can use them to
                  access your account if you lose your authenticator device.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Backup Codes</Label>
                  <Badge variant="secondary">Save these codes</Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {mfaData.backupCodes.map((code, index) => (
                    <div key={index} className="flex gap-2">
                      <Input value={code} readOnly className="font-mono text-sm" />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(code, "code", index)}
                      >
                        {copiedCodes.includes(index) ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}